'use client';

import { m } from 'motion/react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useMagnetic } from '@/hooks/use-magnetic';
import { cn } from '@/lib/utils';

// Motion's m.button has its own drag and animation event signatures that
// clash with React's standard DOM events. Omit those so the spread is safe.
type Props = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onDrag' | 'onDragStart' | 'onDragEnd' | 'onDragEnter' | 'onDragLeave' | 'onDragOver' | 'onDrop'
  | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
> & {
  children: ReactNode;
};

/**
 * Primary CTA. Three things happen on hover:
 *   1. Cursor-magnetic pull (max ±6px) via `useMagnetic`.
 *   2. Brand-glow shadow blooms outward — driven by `--shadow-cta` on rest,
 *      `--shadow-cta-hover` on hover, both defined in `app/globals.css`.
 *   3. The slot's child arrow translates 2px right (handled at the call site
 *      via `group-hover:translate-x-0.5` on the `<svg>`).
 *
 * Shape: 4px radius rectangle (NOT pill) per v3 spec. Owns its own ref via
 * `useMagnetic` — consumers cannot forward a ref because the hook needs
 * exclusive control of the transform target.
 */
export function MagneticButton({ className, children, ...props }: Props) {
  const { ref, x, y } = useMagnetic<HTMLButtonElement>();
  return (
    <m.button
      ref={ref}
      style={{
        x,
        y,
        boxShadow: 'var(--shadow-cta)',
      }}
      className={cn(
        'group relative inline-flex items-center justify-center gap-2 rounded-md',
        'bg-(--color-accent) px-7 py-3.5 font-medium text-[hsl(220_30%_3.5%)]',
        'transition-[box-shadow,background-color,transform] duration-[var(--duration-base)] ease-[var(--ease-out)]',
        'hover:bg-(--color-accent-hover) hover:[box-shadow:var(--shadow-cta-hover)]',
        'hover:-translate-y-0.5 hover:scale-[1.02]',
        'focus-visible:outline-2 focus-visible:outline-(--color-accent) focus-visible:outline-offset-3',
        className,
      )}
      {...props}
    >
      {children}
    </m.button>
  );
}
